//#region Imports
import React, { useState, useEffect, useRef, useMemo } from "react";
import { StyleSheet, Platform, SafeAreaView, Pressable, ScrollView, View, useWindowDimensions, FlatList, ActivityIndicator } from "react-native";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import { useTheme } from '@react-navigation/native';
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import { Card, Searchbar, Provider } from 'react-native-paper';
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox/CustomCheckbox";
import { useClients } from "../../../hooks/useClients";
import { useHRAdminEmployees } from "../../../hooks/useHRAdminEmployees";
import { useTranslation } from "react-i18next";
import { showMessage } from "react-native-flash-message";
import { createCommonStyles } from '../../../styles/commonStyles';
import Swal from 'sweetalert2';
import CustomPaging from "../../../components/CustomPaging/CustomPagining";
import { generateUniqueID } from "../../../components/SurveyBuilderComponent/CreateSurvey/CreateSurveyFunctions";
import uuid from 'react-native-uuid';

const HRAdminClientDetails = ({ setSelectedClient, selectCleintProjectDetails }) => {
    //#region Const Definitions
    const { width, height } = useWindowDimensions();
    const { t } = useTranslation();
    const { colors } = useTheme();
    const styles = makeStyles(colors, width, height);
    const { isLoading, error, GetClients, InsertClient, UpdateClient, DeleteClient, SaveClientEmployeeAuth, GetClientAuthEmployees, DeleteClientEmployeeAuth } = useClients();
    const { getEmployees, isLoading: isLoadingEmployees } = useHRAdminEmployees();
    const [clientName, setClientName] = useState('');
    const [clientCode, setClientCode] = useState('');
    const [authEmployes, setAuthEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState(new Set());
    const clientEmployeeAuthRef = useRef(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [searchQueryClients, setSearchQueryClients] = useState('');
    const [active, setActive] = useState(false);
    const [chargable, setChargable] = useState(false);
    const [clientsDetails, setClientsDetails] = useState([]);
    const [clientAuthEmployees, setClientAuthEmployees] = useState([]);
    const common = createCommonStyles(colors);
    const [isEditing, setIsEditing] = useState(false);
    const [editingClientGuid, setEditingClientGuid] = useState(null);

    //#region Paging Menu
    const [pagedItems, setPagedItems] = useState([]);

    //#region UseEffect
    useEffect(() => {
        findEmployees();
        GetClientsDetails();

    }, []);

    useEffect(() => {
        const employeeIDs = new Set(clientAuthEmployees.map(e => e.employeeId));
        setSelectedEmployees(employeeIDs);
        clientEmployeeAuthRef.current = employeeIDs;
    }, [clientAuthEmployees]);

    useEffect(() => {
        if (searchQuery !== undefined) {
            const delayDebounceFn = setTimeout(() => {
                findEmployees(searchQuery);
            }, 500);
            return () => clearTimeout(delayDebounceFn);
        } else {
            setIsLoading(false);
        };
    }, [searchQuery]);

    const diff = useMemo(() => {
        const original = clientEmployeeAuthRef.current;
        const added = [...selectedEmployees].filter(employeeID => !original.has(employeeID));
        const removed = [...original].filter(employeeID => !selectedEmployees.has(employeeID));
        return { added, removed };
    }, [selectedEmployees]);
    //#endregion

    //#region Helper
    //* Set selected employee for auth in client
    const toggle = (id) => {
        setSelectedEmployees((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const GetClientsDetails = () => {
        GetClients().then((res) => {
            setClientsDetails(res);
        })
            .catch((error) => {
                showMessage({
                    message: t("common:error"),
                    type: "danger",
                    duration: 3000,
                });
            })
    }

    const GetEditClientAuthEmpls = (clientId) => {
        GetClientAuthEmployees(clientId).then((clientAuthEmplRes) => {
            setClientAuthEmployees(clientAuthEmplRes)
        }).catch((error) => {
            showMessage({
                message: t("common:error"),
                type: "warning",
                duration: 3000,
            });
        })

    }

    const findEmployees = (searchQuery = "") => {
        getEmployees(
            [], [], [], [], [], [], searchQuery, "", false, false)
            .then((data) => {
                setAuthEmployees(data);
            })
            .catch((err) => {
                console.error(err.message);
            });
    };


    //#endregion 
    //#region Handle Cancel/Delete/Save/Edit
    const handleCancel = () => {
        setClientName('');
        setClientCode('');
        setActive(false);
        setChargable(false);
        setIsEditing(false);
        setEditingClientGuid(null);
        showMessage({
            message: t("common:formReset"),
            type: "info",
            duration: 2000,
        });
    };

    const handleDelete = (rowguid) => {
        Swal.fire({
            title: t('common:areusure'),
            text: t('common:deleteClientConfirmation'),
            icon: "question",
            showCancelButton: true,
            reverseButtons: true,
            cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">Cancel</text>`,
            cancelButtonColor: '#d3d3d3',
            confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">Delete</text>`,
            confirmButtonColor: '#A0A2FF',
        }).then((result) => {
            if (result.isConfirmed) {
                DeleteClient(rowguid).then((res) => {
                    if (res === 200) {
                        showMessage({
                            message: t("common:deleteSuccess"),
                            type: "success",
                            duration: 3000,
                        });
                        GetClientsDetails();
                    }
                }).catch(() => {
                    showMessage({
                        message: t("common:error"),
                        type: "danger",
                        duration: 3000,
                    });
                });
            }
        });
    }

    const handleSaveOrUpdate = () => {
        if (!clientName.trim() || !clientCode.trim()) {
            showMessage({
                message: t("common:pleaseFillAllFields"),
                type: "warning",
                duration: 3000,
            });
            return;
        }

        if (clientCode.length > 7) {
            showMessage({
                message: t("common:codeMaxLengthWarning"),
                type: "warning",
                duration: 3000,
            });
            return;
        }

        if (isEditing) {
            // Update existing client
            const updatedClient = {
                jobno: clientCode.trim(),
                description: clientName.trim(),
                active: active,
                chargable: chargable,
                rowguid: editingClientGuid,
            };
            // * Update Client 
            UpdateClient(updatedClient).then((res) => {
                if (res === 200) {
                    showMessage({
                        message: t("common:clientUpdatedSuccessfully"),
                        type: "success",
                        duration: 3000,
                    });
                    // GetClientsDetails();
                }
            }).catch(() => {
                showMessage({
                    message: t("common:error"),
                    type: "danger",
                    duration: 3000,
                });
            });
            const { added, removed } = diff;

            //* Save / Update Client Employee Auth
            if (added.length !== 0) {
                const addedRows = added.map(employeeID => ({
                    employeeID,
                    client: clientCode,
                    rowguid: uuid.v4(),
                }));
                SaveClientEmployeeAuth(addedRows).catch((err) => console.error(err));
            }
            if (removed.length !== 0) {
                const removedRows = clientAuthEmployees
                    .filter(e => removed.includes(e.employeeId))
                    .map(e => e.rowguid);
                DeleteClientEmployeeAuth(removedRows).catch((err) => console.error(err));
            }

        } else {
            // Insert new client
            const newClient = {
                jobno: clientCode.trim(),
                description: clientName.trim(),
                active: active,
                chargeable: chargable,
                rowguid: generateUniqueID(),
            };

            InsertClient(newClient).then((res) => {
                if (res === 200) {
                    showMessage({
                        message: t("common:clientCreatedSuccessfully"),
                        type: "success",
                        duration: 3000,
                    });
                    GetClientsDetails();
                }
            }).catch(() => {
                showMessage({
                    message: t("common:error"),
                    type: "danger",
                    duration: 3000,
                });
            });
        }

        // Reset form and editing state
        handleCancel();
        setIsEditing(false);
        setEditingClientGuid(null);
    };

    const handleEdit = (client) => {
        setClientName(client.description);
        setClientCode(client.jobno);
        setActive(client.active);
        setChargable(client.chargable);
        setEditingClientGuid(client.rowguid);
        setIsEditing(true);
        GetEditClientAuthEmpls(client.jobno)
    };
    //#endregion

    // Select to view Client Project screen
    const showClientProjects = (client) => {
        setSelectedClient(client);
        selectCleintProjectDetails();
    }

    //#region Rendering Clients
    // Render each client line
    const renderClient = (client) => {
        return (
            <Pressable onPress={() => showClientProjects(client)}>
                <View key={client.jobno}>
                    <View style={styles.row}>
                        <CustomLabel
                            value={client.description}
                            type='AVAILABLEUSED'
                        />
                        <CustomLabel
                            value={client.jobno}
                            type='AVAILABLEUSED'
                        />
                        <CustomLabel
                            value={client.active ? t('common:active') : t('common:Inactive')}
                            type='AVAILABLEUSED'
                        />
                        <CustomLabel
                            value={client.chargable ? t('common:chargable') : t('common:nonChargable')}
                            type='AVAILABLEUSED'
                        />
                        <CustomButton
                            iconName='pencil-outline'
                            onPress={() => handleEdit(client)}
                            show='HOME_BELL'
                            type='HOME_BELL'
                            textTop='2'
                            sz={20}
                        />
                        <CustomButton
                            onPress={() => handleDelete(client.rowguid)}
                            iconName="trash-outline"
                            show="HOME_BELL"
                            type="HOME_BELL"
                            textTop="2"
                            sz={20}
                        />
                    </View>
                </View>
            </Pressable>
        );
    };

    // Render all clients
    const renderClients = (clients) => {

        const q = searchQueryClients.toLowerCase();
        const filtered = q === ''
            ? clients
            : clients.filter(client =>
                client.jobno?.toString().toLowerCase().includes(q) ||
                client.description?.toLowerCase().includes(q)
            );

        return (
            <Provider>
                <Searchbar
                    placeholder={t("common:search")}
                    onChangeText={setSearchQueryClients}
                    value={searchQueryClients}
                    style={styles.searchbar}
                    inputStyle={styles.searchInput}
                    iconColor={colors.green}
                    placeholderTextColor="#A7A8AA"
                    theme={{ colors: { primary: colors.green } }}
                />
                {filtered.map(client => (
                    <Provider key={client.ClientCode}>
                        <Card style={[styles.cardstyle]}>
                            <Card.Content>
                                {renderClient(client)}
                            </Card.Content>
                        </Card>
                    </Provider>
                ))}
            </Provider>
        );
    };
    //#endregion

    return (
        <SafeAreaView style={styles.scroll}>
            <SafeAreaView style={styles.main}>
                <SafeAreaView style={styles.row}>
                    {/* Left column: create/edit form card */}
                    <ScrollView style={styles.leftColumn}>
                        <Card style={styles.cardstyle}>
                            <Card.Content>
                                <CustomLabel
                                    value={isEditing ? t('common:editClient') : t('common:createNewClient')}
                                    type="SUBTITLE"
                                    style={{ marginBottom: 12 }}
                                />
                                <View style={common.divider} />
                                <View style={{ marginTop: 20 }}>
                                    <CustomInput
                                        value={clientName}
                                        setValue={setClientName}
                                        placeholder={t('common:clientName')}
                                        type="MAININPUT"
                                    />
                                </View>
                                <View style={{ marginTop: 20 }}>
                                    <CustomInput
                                        value={clientCode}
                                        setValue={setClientCode}
                                        placeholder={t('common:clientCode')}
                                        type="MAININPUT"
                                    />
                                </View>
                                <View style={styles.CheckBoxRow}>
                                    <CustomCheckbox
                                        label={t('common:active')}
                                        value={active}
                                        onValueChange={setActive}
                                    />
                                    <CustomCheckbox
                                        label={t('common:chargable')}
                                        value={chargable}
                                        onValueChange={setChargable}
                                    />
                                </View>
                                {/* Selected chips */}
                                {isEditing && <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                                    {authEmployes?.map(e => (
                                        selectedEmployees?.has(e.employeeID) &&
                                        <Pressable
                                            key={e.id}
                                            onPress={() => toggle(e.employeeID)}
                                            style={{
                                                flexDirection: 'row', alignItems: 'center', gap: 6,
                                                backgroundColor: colors.green, borderRadius: 16,
                                                paddingVertical: 4, paddingHorizontal: 10,
                                            }}>
                                            <CustomLabel
                                                value={`${e.name}   x `}
                                                type="AVAILABLEUSED_SECONDARY"
                                                fontSize={14}
                                            />
                                        </Pressable>
                                    ))}
                                </View>}

                                {isEditing && <FlatList
                                    data={authEmployes}
                                    keyExtractor={(item) => String(item.employeeID)}
                                    style={{ maxHeight: 280 }}
                                    ListEmptyComponent={
                                        isLoadingEmployees ? (
                                            <ActivityIndicator
                                                size="small"
                                                color={colors.green}
                                                style={{ paddingVertical: 24 }}
                                            />
                                        ) : (
                                            <CustomLabel
                                                value={t("common:NOFY")}
                                                type="MINORTITLE"
                                            />
                                        )
                                    }
                                    ListHeaderComponent={
                                        <View >
                                            {/* search */}
                                            <CustomInput
                                                value={searchQuery || ""}
                                                setValue={setSearchQuery}
                                                type='SELECT_EMPLOYEE'
                                                placeholder={`${t('common:search')}...`}
                                                fontSize={12}
                                            />
                                            <View style={common.divider} />
                                        </View>
                                    }
                                    keyboardShouldPersistTaps="handled"
                                    renderItem={({ item }) => {
                                        const isSelected = selectedEmployees?.has(item.employeeID);
                                        return (

                                            <Pressable
                                                onPress={() => toggle(item.employeeID)}
                                                style={{
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    paddingVertical: 9,
                                                    paddingHorizontal: 12,
                                                    borderBottomWidth: 0.5,
                                                    borderColor: '#E4EDD6',
                                                    opacity: isSelected ? 0.45 : 1,
                                                }}
                                            >
                                                {isSelected && <Ionicons name="checkmark" size={14} color={colors.green} />}
                                                <CustomLabel
                                                    value={item.name}
                                                    type='AVAILABLEUSED_SECONDARY'
                                                    align='flex-start'
                                                />
                                                <Ionicons
                                                    name={isSelected ? 'checkmark' : 'add'}
                                                    size={17}
                                                    color={isSelected ? colors.green_dark : colors.green}
                                                />

                                            </Pressable>
                                        );
                                    }}
                                />
                                }
                            </Card.Content>
                            <Card.Actions>
                                <View
                                    style={[
                                        styles.buttons,
                                    ]}
                                >
                                    <CustomButton
                                        text={isEditing ? t("common:update") : t("common:save")}
                                        onPress={handleSaveOrUpdate}
                                        type="BUTTON_SAVE"
                                        sz={15}
                                        style={styles.buttons}
                                        isGradient={true}
                                    />
                                    <CustomButton
                                        text={t("common:cancel")}
                                        onPress={handleCancel}
                                        type="BUTTON_CANCEL"
                                        sz={15}
                                        style={styles.buttons}
                                    />
                                </View>
                            </Card.Actions>
                        </Card>
                    </ScrollView>
                    {/* Right column */}
                    {isLoading ?
                        <View style={{ flexDirection: 'column', width: '70%' }}>
                            <ActivityIndicator animating={true} color={colors.green} />
                        </View> :
                        <View style={{ flexDirection: 'column', width: '70%' }}>
                            <ScrollView style={Platform.OS === 'web' && width > 1200 ? styles.rightColumnNormal : styles.rightColumn} >
                                {/* Render clients */}
                                {renderClients(pagedItems)}
                            </ScrollView>
                            {/* Show Paging Menu */}
                            <CustomPaging
                                items={clientsDetails}
                                itemsPerPage={20}
                                onPageItemsChange={setPagedItems}
                            />
                        </View>
                    }
                </SafeAreaView>
            </SafeAreaView>
        </SafeAreaView>
    );
}

const makeStyles = (colors, width, height) =>
    StyleSheet.create({
        main: {
            flex: 1,
            marginTop: "1%",
            marginLeft: '2%',
            marginRight: '2%',
        },
        scroll: {
            flex: 1,
        },
        leftColumn: {
            flex: 2,
            marginLeft: Platform.OS === 'web' ? '2%' : '1%',
            maxWidth: width * 0.25,
            marginRight: Platform.OS === 'web' ? 20 : '1%',
            marginTop: height < 600 ? height * 40 / 100 : 10
        },
        rightColumnNormal: {
            flex: 1,
            marginTop: 10,
        },
        rightColumn: {
            width: width * 95 / 100,
            marginTop: height < 600 ? height * 40 / 100 : 10,
        },
        cardstyle: {
            marginLeft: (Platform.OS === 'web' && width > 1200 ? '2%' : '4%'),
            marginRight: (Platform.OS === 'web' && width > 1200 ? '2%' : '4%'),
            marginTop: 10,
            marginBottom: 10,
            borderRadius: 5,
            elevation: 3,
            shadowColor: colors.card_line,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.1,
            shadowRadius: 5,
            backgroundColor: colors.background_card,
            flex: 'none',
        },
        row: {
            flex: 1,
            flexDirection: "row",
        },
        buttons: {
            flexDirection: "row",
            justifyContent: "flex-end",
            alignSelf: "flex-end",
        },
        CheckBoxRow: {
            flex: 1,
            flexDirection: "row",
            marginVertical: 20
        },

        searchbar: {
            marginHorizontal: 12,
            marginVertical: 8,
            borderRadius: 4,               // Deloitte tends toward sharp, minimal corners
            backgroundColor: '#FFFFFF',
            borderWidth: 1,
            borderColor: colors.green,
            elevation: 2,                  // subtle shadow (Android)
            shadowColor: '#000',           // iOS shadow
            shadowOpacity: 0.1,
            shadowRadius: 3,
            shadowOffset: { width: 0, height: 1 },
        },
        searchInput: {
            color: '#000000',              // Deloitte black text
            fontSize: 14,
        },
    });

export default HRAdminClientDetails;
